import React, { useState, useEffect } from 'react';
import {
    Box, Typography, IconButton, Button, Chip, Card, CardContent,
    CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    Add as AddIcon, PictureAsPdf as PdfIcon, Edit as EditIcon, PersonSearch as PersonSearchIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosInstance from '../../../axiosInstance';
import extEquipmentService from '../../../services/extEquipmentService';
import PdfPreviewModal from '../../jha/pdf/PdfPreviewModal';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import { initializeDB } from '../../../config/db';
import { EMPLOYEE_STORE } from '../../../config/constants';
import { FORM_TYPES, EQUIPMENT_TYPES } from './constants';

export default function ExtEquipmentProfile() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const qrToken = searchParams.get('qrToken');

    const [info, setInfo] = useState(null);
    const [inspections, setInspections] = useState([]);
    const [dailyChecks, setDailyChecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfOpen, setPdfOpen] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfFilename, setPdfFilename] = useState('');
    const [pdfLoading, setPdfLoading] = useState(false);

    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [contactEmpModalOpen, setContactEmpModalOpen] = useState(false);
    const [contactSaving, setContactSaving] = useState(false);
    const [contactName, setContactName] = useState('');
    const [contactPhone, setContactPhone] = useState('');

    const openPdf = async (url, filename) => {
        setPdfLoading(true); setPdfOpen(true); setPdfFilename(filename); setPdfBlob(null);
        try { const res = await axiosInstance.get(url, { responseType: 'blob' }); setPdfBlob(res.data); }
        catch (e) { console.error('PDF load error:', e); }
        setPdfLoading(false);
    };

    const openContactModal = () => {
        const t = info?.tag;
        if (!t) return;
        setContactName(t.assignedEmployeeName || t.assignedName || '');
        setContactPhone(t.assignedEmployeePhone || t.assignedPhone || '');
        setContactModalOpen(true);
    };

    const handleContactEmpSelect = async (emp) => {
        const empName = emp.FullName || emp.fullName || '';
        let empPhone = emp.Phone || emp.phone || '';
        if (!empPhone && empName) {
            try {
                const db = await initializeDB();
                const allEmps = await db.getAll(EMPLOYEE_STORE);
                const found = allEmps?.find(e => (e.FullName || e.fullName) === empName);
                if (found) empPhone = found.Phone || found.phone || '';
            } catch (e) { /* ignore */ }
        }
        setContactName(empName);
        setContactPhone(empPhone);
        setContactEmpModalOpen(false);
    };

    const handleContactSave = async () => {
        if (!contactName.trim()) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Select an employee' }); return;
        }
        try {
            setContactSaving(true);
            const res = await extEquipmentService.updateContact(qrToken, {
                custom: false,
                assignedEmployeeName: contactName,
                assignedEmployeePhone: contactPhone,
            });
            setInfo(prev => prev ? ({
                ...prev,
                tag: {
                    ...prev.tag,
                    assignedName: res.assignedName,
                    assignedPhone: res.assignedPhone,
                    assignedEmployeeName: res.assignedEmployeeName,
                    assignedEmployeePhone: res.assignedEmployeePhone,
                    assignedCustomName: res.assignedCustomName,
                    assignedCustomPhone: res.assignedCustomPhone,
                },
            }) : prev);
            setContactModalOpen(false);
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.error || 'Failed to update contact' });
        } finally {
            setContactSaving(false);
        }
    };

    useEffect(() => {
        if (!qrToken) return;
        (async () => {
            setLoading(true);
            try {
                const [infoRes, inspRes, dailyRes] = await Promise.all([
                    extEquipmentService.getInfo(qrToken),
                    extEquipmentService.getInspections(qrToken),
                    extEquipmentService.getDailyChecks(qrToken),
                ]);
                setInfo(infoRes);
                setInspections(inspRes.data || []);
                setDailyChecks(dailyRes.data || []);
            } catch (e) { console.error(e); }
            setLoading(false);
        })();
    }, [qrToken]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;
    if (!info?.tag) return <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="error">Tag not found</Typography></Box>;

    const { tag, needsDailyCheck } = info;
    const formTypeConfig = FORM_TYPES.find(f => f.value === tag.formType);
    const eqTypeConfig = EQUIPMENT_TYPES.find(t => t.value === tag.equipmentType);

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start" size="small"><ArrowBackIcon /></IconButton>
                        <Chip label={tag.label} size="small" sx={{ fontWeight: 700, fontFamily: 'monospace' }} />
                        {eqTypeConfig && <Chip label={eqTypeConfig.label} size="small" sx={{ backgroundColor: eqTypeConfig.color, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />}
                        {tag.linkedDescription && <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', ml: 0.5 }}>{tag.linkedDescription}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {tag.linked && needsDailyCheck && (
                            <Button variant="contained" size="small" startIcon={<AddIcon />}
                                onClick={() => navigate(`/operations/ext-equipment/daily-check?qrToken=${qrToken}`)}
                                sx={{ fontWeight: 700, backgroundColor: '#1976d2', fontSize: '0.75rem' }}>
                                Daily Check
                            </Button>
                        )}
                        {tag.linked && !needsDailyCheck && (
                            <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Today Done" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                {!tag.linked && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Not linked. <Button size="small" sx={{ ml: 1 }} onClick={() => navigate(`/operations/ext-equipment/link?qrToken=${qrToken}`)}>Link Now</Button>
                    </Alert>
                )}

                {/* Top row: Equipment Info + Tag Info side by side */}
                {tag.linked && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 1 }}>
                        <Card>
                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem', mb: 0.5 }}>Equipment</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.25, fontSize: '0.78rem' }}>
                                    {tag.equipmentType && <span><strong>Type:</strong> {tag.unitSubType || tag.equipmentType}</span>}
                                    {tag.manufacturer && <span><strong>MFR:</strong> {tag.manufacturer}</span>}
                                    {tag.model && <span><strong>Model:</strong> {tag.model}</span>}
                                    {tag.serialNumber && <span><strong>S/N:</strong> {tag.serialNumber}</span>}
                                    {tag.equipmentNumber && <span><strong>ID:</strong> {tag.equipmentNumber}</span>}
                                    {tag.owner && <span><strong>Owner:</strong> {tag.owner}</span>}
                                    {tag.location && <span><strong>Location:</strong> {tag.location}</span>}
                                </Box>
                                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #f0f0f0', fontSize: '0.78rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <strong>Contact:</strong>{' '}
                                        {tag.assignedName ? (
                                            <>
                                                {tag.assignedName}
                                                {tag.assignedPhone && (
                                                    <> — <a href={`tel:${tag.assignedPhone}`} style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 600 }}>{tag.assignedPhone}</a></>
                                                )}
                                            </>
                                        ) : (
                                            <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>
                                        )}
                                    </Box>
                                    <Button size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                        onClick={openContactModal}
                                        sx={{ fontSize: '0.65rem', py: 0.1, px: 0.75, minWidth: 0, flexShrink: 0, lineHeight: 1.4 }}>
                                        Change
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>Status</Typography>
                                    {formTypeConfig && <Chip label={formTypeConfig.label} size="small" sx={{ backgroundColor: formTypeConfig.color, color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />}
                                </Box>
                                <Box sx={{ fontSize: '0.78rem', mt: 0.5 }}>
                                    {tag.linkedBy && <Box><strong>Linked:</strong> {tag.linkedBy}</Box>}
                                    <Box><strong>Inspections:</strong> {inspections.length} | <strong>Daily checks:</strong> {dailyChecks.length}</Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* Bottom row: Inspections + Daily Checks side by side */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                    {/* Inspections */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                                Earlier Inspections ({inspections.length})
                            </Typography>
                            {inspections.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">No inspections yet</Typography>
                            ) : (
                                <TableContainer sx={{ maxHeight: 300 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>By</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5, width: 40 }}></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {inspections.map((insp) => {
                                                return (
                                                    <TableRow key={insp.id} hover>
                                                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                                                            <strong>{insp.inspectionDate}</strong>
                                                            {insp.isInitial && <Chip label="I" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.55rem', backgroundColor: '#e3f2fd', color: '#1565c0' }} />}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>{insp.inspectorName || insp.submittedByName || '—'}</TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            <IconButton size="small" onClick={() => openPdf(`/ext-equipment/inspection/${insp.id}/pdf`, `inspection-${insp.inspectionDate}.pdf`)}>
                                                                <PdfIcon sx={{ fontSize: 16, color: '#e53935' }} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>

                    {/* Daily Checks */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                                    Daily Checks — 30 Days ({dailyChecks.length})
                                </Typography>
                                {tag.linked && needsDailyCheck && (
                                    <Button variant="contained" size="small" startIcon={<AddIcon />}
                                        onClick={() => navigate(`/operations/ext-equipment/daily-check?qrToken=${qrToken}`)}
                                        sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.25, backgroundColor: '#1976d2' }}>
                                        Today
                                    </Button>
                                )}
                                {tag.linked && !needsDailyCheck && (
                                    <Chip icon={<CheckCircleIcon sx={{ fontSize: 12 }} />} label="Done" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                                )}
                            </Box>
                            {dailyChecks.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">No daily checks yet</Typography>
                            ) : (
                                <TableContainer sx={{ maxHeight: 300 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>Operator</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>Notes</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5, width: 40 }}></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dailyChecks.map((check) => (
                                                <TableRow key={check.id} hover>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <CheckCircleIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                                            <strong>{check.checkDate}</strong>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>{check.operatorName || check.submittedByName || '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.72rem', py: 0.5, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {check.notes || '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5 }}>
                                                        <IconButton size="small" onClick={() => openPdf(`/ext-equipment/daily-check/${check.id}/pdf`, `daily-check-${check.checkDate}.pdf`)}>
                                                            <PdfIcon sx={{ fontSize: 16, color: '#e53935' }} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            <PdfPreviewModal open={pdfOpen} onClose={() => { setPdfOpen(false); setPdfBlob(null); }}
                pdfBlob={pdfBlob} filename={pdfFilename} loading={pdfLoading} />

            <Dialog open={contactModalOpen} onClose={() => !contactSaving && setContactModalOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 1, fontSize: '1rem', fontWeight: 700 }}>Change Equipment Contact</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField label="Employee *" size="small" fullWidth value={contactName}
                                    InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }}
                                    onClick={() => setContactEmpModalOpen(true)}
                                    sx={{ cursor: 'pointer', '& .MuiInputBase-input': { cursor: 'pointer' } }}
                                    placeholder="Tap to select..." />
                                <IconButton onClick={() => setContactEmpModalOpen(true)} color="primary" size="small">
                                    <PersonSearchIcon />
                                </IconButton>
                            </Box>
                        </Grid>
                        {contactPhone && (
                            <Grid item xs={12}>
                                <TextField label="Phone" size="small" fullWidth value={contactPhone}
                                    disabled InputLabelProps={{ shrink: true }} />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setContactModalOpen(false)} disabled={contactSaving}>Cancel</Button>
                    <Button onClick={handleContactSave} variant="contained" disabled={contactSaving}>
                        {contactSaving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <SelectSingleEmployeeModal open={contactEmpModalOpen} onClose={() => setContactEmpModalOpen(false)}
                onSelect={handleContactEmpSelect} />
        </Box>
    );
}
